import L from 'leaflet';
import forEach from 'lodash/forEach';
import moment from 'moment';

// https://mdx.meteotest.ch/info/samples
const TERMS = {
	D: 0,
	V: 8,
	N: 14,
	A: 18,
};

const DEFAULTS = {
	startTime: moment().subtract(12, 'h').valueOf(),
	stopTime: moment().add(36, 'h').valueOf(),
	currentTime: moment().valueOf(),

	tickStep: [[10, 'm'], [1, 'h']],
	tickDelay: 500,
	tickModifier: when => when,

	nowDelay: 1000,
	shouldLoop: false,
};

/**
 * @extends {L.Evented}
 */
class TimeManagement extends L.Evented {
	/**
	 * @param {Widget} widget
	 * @param {object} options
	 * @param {moment} options.startTime
	 * @param {moment} options.stopTime
	 * @param {moment} options.currentTime
	 * @param {array} options.tickStep [beforeAndCurrent, afterCurrent] as [value, unit]
	 * @param {number} options.tickDelay
	 * @param {number} options.nowDelay
	 * @param {boolean} options.shouldLoop
	 */
	constructor(widget, options) {
		super();

		this.widget = widget;
		this.options = Object.assign({}, DEFAULTS, options);
		this.options.currentTime = this.options.tickModifier(this.options.currentTime);
		this.addEventParent(widget);

		this._layers = {};
		this._tickInterval = null;
		this._nowTime = Date.now();
		this._isPlaying = false;
		this._hasEnded = false;

		this._nowInterval = setInterval(
			() => this.updateNow(),
			this.options.nowDelay
		);
	}

	/**
	 *
	 */
	tick() {
		let step = this.options.currentTime > this._nowTime
			? this.options.tickStep[1]
			: this.options.tickStep[0];

		let next = moment(this.options.currentTime);
		next = next.add(step[0], step[1]);
		next = this.options.tickModifier(next);

		if (next > this.options.stopTime) {
			if (this.options.shouldLoop) {
				this.options.currentTime = this.options.startTime;
			} else {
				clearInterval(this._tickInterval);

				this._tickInterval = null;
				this._isPlaying = false;
				this._hasEnded = true;
			}

			this.fire('TIME_PLAYBACK_ENDED');
		} else {
			this.options.currentTime = next;

			forEach(this._layers, layer => layer.setTime(this.options.currentTime));

			this.fire('TIME_PLAYBACK_TICKED', { current: this.options.currentTime });
		}
	}

	/**
	 *
	 */
	updateNow() {
		this._nowTime = Date.now();

		this.fire('TIME_NOW_UPDATED', { now: this._nowTime });
	}

	/**
	 * @param {L.Layer} layer
	 */
	addLayer(layer) {
		if (this._layers[layer.options.name] === undefined) {
			this._layers[layer.options.name] = layer;
			layer.setTime(this.options.currentTime);

			this.fire('TIME_LAYER_ADDED', { layer });
		}
	}

	/**
	 * @param {L.Layer} layer
	 */
	removeLayer(layer) {
		if (this._layers[layer.options.name] !== undefined) {
			delete this._layers[layer.options.name];

			this.fire('TIME_LAYER_REMOVED', { layer });
		}
	}

	/**
	 *
	 */
	play() {
		if (!this._isPlaying) {
			if (this._hasEnded) {
				this.options.currentTime = this.options.startTime;
			}

			setTimeout(this.tick.bind(this), 0);
			this._tickInterval = setInterval(
				() => this.tick(),
				this.options.tickDelay
			);

			this._hasEnded = false;
			this._isPlaying = true;

			this.fire('TIME_PLAYBACK_STARTED_RESUMED');
		}
	}

	/**
	 *
	 */
	pause() {
		if (this._isPlaying) {
			clearInterval(this._tickInterval);

			this._tickInterval = null;
			this._isPlaying = false;

			this.fire('TIME_PLAYBACK_PAUSED');
		}
	}

	/**
	 *
	 */
	toggle() {
		if (this._isPlaying) {
			this.pause();
		} else {
			this.play();
		}
	}

	/**
	 * @param {moment} when Moment.js instance
	 */
	setMoment(when) {
		if (moment.isMoment(when) && !when.isSame(this.options.currentTime)) {
			this.setTime(when.valueOf());
		}
	}

	/**
	 * @param {number} timestamp Javascript timestamp (ms)
	 */
	setTime(timestamp) {
		if (timestamp !== this.options.currentTime) {
			timestamp = Math.max(timestamp, this.options.startTime);
			timestamp = Math.min(timestamp, this.options.stopTime);

			timestamp = this.options.tickModifier(timestamp);

			this.options.currentTime = timestamp;

			forEach(this._layers, layer => {
				layer.setTime(this.options.currentTime)
			});

			this.fire('TIME_PLAYBACK_SEEKED', { current: this.options.currentTime });
		}
	}

	/**
	 * @param {number} offset Day offset from today.
	 * @param {string} term The "term" (D, V, N, A).
	 */
	setTerm(offset, term) {
		offset = parseInt(offset, 10);
		term = term.toUpperCase();

		let when = moment();

		when.add(offset, 'd');
		when.hours(TERMS[term]);

		this.setTime(when.valueOf());
	}

	/**
	 *
	 */
	destroy() {
		this.removeEventParent(this.widget);

		clearInterval(this._nowInterval);
		clearInterval(this._tickInterval);
	}
}

export function timeManagement(widget, options) {
	return new TimeManagement(widget, options);
}

export default TimeManagement;
